/* eslint-disable no-underscore-dangle */
// src/components/DataTable.js

import { Box, Checkbox, IconButton, Modal, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { ReactNode, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import FilledStart from 'src/icons/FilledStar';
import FilterIcon from 'src/icons/filter';
import SortAscending from 'src/icons/SortAscending';
// import { SortAtoZ } from 'src/icons/SortAtoZ';
import SortDescending from 'src/icons/SortDescending';
// import { SortZtoA } from 'src/icons/SortZtoA';
import Star from 'src/icons/Star';
import useGenericStyles from 'src/styles/useGenericStyles';
// import { CustomButton2 } from '../widgets/buttons/CustomButton2';
// import ColumnCustomizer from './ColumnCustomizer';

interface Sort {
  field: string;
  order: string;
}
interface FilterOption {
  value: string;
  label: string;
}

interface TableProps {
  heading?: string | ReactNode;
  columns: any;
  data: any;
  page?: number;
  limit?: number;
  handleLimit?: (limit: number) => void;
  handlePage?: (page: number) => void;
  totalRecords?: number;
  loading?: boolean;
  showCheckBoxes?: boolean;
  showFavouriteButtons?: boolean;
  alwaysShowSortIcons?: boolean;
  getFilterOptions?: (column: string) => FilterOption[];
  handleSort?: (columnField: string) => void;
  sort?: Sort | any;
  handleApplyFilter?: (
    columnName: string,
    filterValue: string,
    filterName: string,
    checked: boolean
  ) => void;
  filters?: any;
  navigateUrl?: string;
  navigateToKey?: boolean;
  favourites?: Array<any>;
  handleClickFavourite?: (favId: string) => void;
  selectRowData?: boolean; // This prop if set to true will make the checkbox select whole row objects rather than only id's
  actionButtonCallback?: (values: any) => void; // This callback is fired when we click on the button in the heading, if its provided
  actionButtonLabel?: string;
  showPagination?: boolean;
  handleOnClick?: (rowData: any) => void; // This callback is fired when we click on the table row, if its provided
  customizableColumns?: boolean;
  onColumnChange?: (visibleColumns: string[]) => void;
  defaultVisibleColumns?: string[];
  storageKey?: string;
  maxHeight?: string | number;
  getRowStyle?: (row: any) => React.CSSProperties;
  /** Body cell padding. Defaults to compact `4px 10px`. */
  bodyCellPadding?: string;
  /** Header cell padding. Defaults to `8px 10px`. */
  headerCellPadding?: string;
}

export const TableRowsLoader = ({
  rowsNum,
  columnsNum,
}: {
  rowsNum: number;
  columnsNum: number;
}) => (
  <TableBody>
    {Array.from({ length: rowsNum }, (_, rowNumber) => rowNumber + 1).map((rowNumber) => (
      <TableRow key={`loader-row-${rowNumber}`}>
        <TableCell colSpan={columnsNum} style={{
          padding: '2px 10px'
        }}>
          <Skeleton animation="wave" variant="text" />
        </TableCell>
      </TableRow>
    ))}
  </TableBody>
);

const useStyles = makeStyles(() => ({
  reportingTable: {
    '& .MuiTableCell-root': {
      padding: '5px 10px 5px 0px'
    },
    '& td:nth-child(1)': {
      // '& .MuiTableCell': {
      paddingLeft: '10px'
      // }
    },
    '& .MuiTableCell-head': {
      verticalAlign: 'middle',
      padding: '10px 5px 10px 0px'
    },
    '& th:nth-child(1)': {
      // '& .MuiTableCell': {
      paddingLeft: '10px'
      // }
    },
    '& th:nth-child(6)': {
      '& .MuiBox-root': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: 100,
        display: 'inline-block',
        height: '20px'
      },
    },
  },
}));
const CustomizedTable = ({
  // heading,
  columns,
  data,
  handlePage,
  handleLimit,
  page,
  limit,
  totalRecords,
  loading,
  getFilterOptions,
  showCheckBoxes,
  showFavouriteButtons,
  handleSort,
  sort,
  handleApplyFilter,
  filters,
  navigateUrl,
  navigateToKey,
  favourites,
  handleClickFavourite,
  selectRowData = false,
  alwaysShowSortIcons = false,
  // actionButtonCallback,
  // actionButtonLabel,
  showPagination = true,
  handleOnClick = () => { },
  // customizableColumns = false,
  // onColumnChange,
  defaultVisibleColumns,
  storageKey = 'table',
  maxHeight,
  getRowStyle,
  bodyCellPadding = '4px 10px',
  headerCellPadding = '8px 10px',
}: TableProps) => {
  const navigate = useNavigate();

  const reportClass = useStyles();

  const [modalOpen, setModalOpen] = useState(false);
  const [currentFilterColumn, setCurrentFilterColumn] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  // const theme = useTheme();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null); // State to track the position of the filter icon
  const [filterOptions, setFilterOptions] = useState([]); // State to hold filter options for the current column

  const classes = useGenericStyles();

  const [visibleColumns] = useState<string[]>(
    defaultVisibleColumns || columns.map(col => col.field)
  );

  // const handleColumnChange = (newVisibleColumns: string[]) => {
  //   setVisibleColumns(newVisibleColumns);
  //   if (onColumnChange) {
  //     onColumnChange(newVisibleColumns);
  //   }
  // };

  const filteredColumns = useMemo(() => columns
    .slice()
    .sort((a, b) => {
      const savedOrder = localStorage.getItem(`${storageKey}ColumnOrder`);
      if (!savedOrder) return 0;

      const orderFields = JSON.parse(savedOrder);
      const aIndex = orderFields.indexOf(a.field);
      const bIndex = orderFields.indexOf(b.field);

      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    })
    .filter(col => visibleColumns.includes(col.field)), [columns, storageKey, visibleColumns]);

  const getNavigationPath = useCallback((row: any) => {
    if (!navigateUrl) {
      return '';
    }

    return `${navigateUrl}/${navigateToKey ? row.caseKey : (row?.id ?? row?._id)}`;
  }, [navigateUrl, navigateToKey]);

  const handleRowClick = useCallback((row: any) => {
    if (navigateUrl) {
      const path = getNavigationPath(row);
      if (path) {
        navigate(path);
      }
    }

    handleOnClick(row);
  }, [navigate, navigateUrl, getNavigationPath, handleOnClick]);

  const handleChangePage = useCallback((event: any, _page: number) => {
    handlePage(_page);
  }, [handlePage]);

  const handleChangeLimit = useCallback((event: any) => {
    handleLimit(parseInt(event.target.value, 10));
    handlePage(0);
  }, [handleLimit, handlePage]);

  const handleOpenFilterModal = useCallback((event, column) => {
    setCurrentFilterColumn(column.headerName);
    setFilterOptions(getFilterOptions(column.field)); // Get filter options for the column

    const anchorRect = event.currentTarget.getBoundingClientRect();
    const modalWidth = 214; // Set the modal width to 214px (the width of the filter modal)
    const windowWidth = window.innerWidth;
    const adjustedLeftPosition = anchorRect.left + modalWidth > windowWidth
      ? windowWidth - modalWidth - 20 // Adjust to stay within the viewport with some padding
      : anchorRect.left;

    setFilterAnchorEl({
      top: anchorRect.bottom + window.scrollY,
      left: adjustedLeftPosition + window.scrollX,
    });

    setModalOpen(true);
  }, [getFilterOptions]);

  const handleCloseFilterModal = useCallback(() => {
    setModalOpen(false);
    setFilterAnchorEl(null);
  }, []);

  const renderCell = (column, row) => {
    if (column.render) {
      return column.render(row[column.field], row);
    }
    return row[column.field];
  };

  const renderSortIcon = (field) => {
    if (sort?.field === field) {
      return sort.order === 'asc' ? <SortAscending /> : <SortDescending />;
    }
    return alwaysShowSortIcons ? <SortAscending style={{ opacity: 0.3 }} /> : null;
  };

  const handleSelectRow = (rowObjorId) => {
    // eslint-disable-next-line consistent-return
    setSelectedRows((prevSelectedRows) => {
      if (selectRowData) {
        const isSelected = prevSelectedRows?.find(
          (selectedRow) => selectedRow.id === rowObjorId.id
        );
        if (isSelected) {
          return prevSelectedRows?.filter(
            (selectedRow) => selectedRow.id !== rowObjorId.id
          );
        }
        return [...prevSelectedRows, rowObjorId];
      }
      // in case its selecting id's only
      setSelectedRows((prevSelected_Rows) => {
        if (prevSelected_Rows.includes(rowObjorId)) {
          return prevSelected_Rows.filter((rowId) => rowId !== rowObjorId);
        }
        return [...prevSelected_Rows, rowObjorId];
      });
    });
  };

  // Handle action button click
  // const handleActionButtonClick = () => {
  //   if (actionButtonCallback) {
  //     actionButtonCallback(selectedRows);
  //     setSelectedRows([]);
  //   }
  // };

  const isSelected = useCallback((rowIdorObj) =>
    selectRowData
      ? selectedRows?.some((selectedRow) => selectedRow.id === rowIdorObj.id)
      : selectedRows?.includes(rowIdorObj), [selectRowData, selectedRows]);

  const isFavourite = useCallback((id) => favourites?.includes(id), [favourites]);

  const handleSelectAllRows = useCallback(() => {
    if (selectedRows?.length === data?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(selectRowData ? data : data?.map((row) => row.id));
    }
  }, [selectedRows, data, selectRowData]);

  return (
    <Paper style={{ borderRadius: '16px', overflow: 'hidden' }}>
      {/* {(heading || actionButtonLabel || customizableColumns) && (
        <Box
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            padding: '18px 24px',
            justifyContent: 'space-between',
          }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography className={classes.smallHeading} color="#1F2D3D">
              {heading}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {customizableColumns && (
              <ColumnCustomizer
                columns={columns}
                visibleColumns={visibleColumns}
                onColumnChange={handleColumnChange}
                storageKey={storageKey}
              />
            )}
            {actionButtonLabel && actionButtonCallback && (
              <CustomButton2
                disabled={selectedRows?.length < 1}
                onClick={handleActionButtonClick}
                backgroundColor={theme.palette.primary.main}>
                {actionButtonLabel}
              </CustomButton2>
            )}
          </Box>
        </Box>
      )} */}
      <TableContainer
        className={columns[5]?.field === "sprintAllocatedTime" ? reportClass.reportingTable : ''}
        style={{ maxHeight, borderRadius: '16px' }}
      >
        <Table stickyHeader style={{ width: '100%' }}>
          <TableHead style={{
            textWrap: 'nowrap',
          }}>
            <TableRow>
              {showCheckBoxes && (
                <TableCell
                  style={{
                    borderRight: '1px solid #EDF2F7',
                    padding: '4px',
                    width: '35px',
                    minWidth: '35px',
                    maxWidth: '35px',
                    textAlign: 'center'
                  }}
                >
                  <Checkbox
                    indeterminate={
                      selectedRows?.length > 0 &&
                      selectedRows?.length < data?.length
                    }
                    checked={
                      selectedRows?.length > 0 &&
                      selectedRows?.length === data?.length
                    }
                    onChange={handleSelectAllRows}
                    style={{
                      width: '15px',
                      minWidth: '15px',
                      padding: '2px'
                    }}
                  />
                  {/* </Box> */}
                </TableCell>
              )}
              {showFavouriteButtons && (
                <TableCell
                  padding="checkbox"
                  style={{
                    padding: '0 10px',
                    borderRight: '1px solid #EDF2F7',
                  }}>
                  <Checkbox checked checkedIcon={<FilledStart />} disabled />
                </TableCell>
              )}
              {filteredColumns.map((column) => (
                // <TableCell
                //   key={column.field}
                //   style={{
                //     maxWidth: column.maxWidth || 'auto',
                //     width: column.width,
                //     textTransform: 'uppercase',
                //     // textWrap: 'nowrap',
                //     padding: '8px 10px',
                //   }}>
                //   <Box
                //     component="span"
                //     style={{
                //       cursor: column.sortable ? 'pointer' : 'default',
                //       userSelect: 'none',
                //     }}
                //     {...(column.sortable && {
                //       onClick: () => handleSort(column.field),
                //     })}>
                //     {column.headerName}
                //   </Box>
                //   {column.filterable && (
                //     <IconButton
                //       onClick={(event) => handleOpenFilterModal(event, column)}
                //       style={{
                //         padding: 0,
                //         marginLeft: '5px',
                //       }}>
                //       <FilterIcon />
                //     </IconButton>
                //   )}
                //   {column.sortable && (
                //     <IconButton
                //       disableRipple
                //       disableTouchRipple
                //       style={{
                //         padding: '0 5px',
                //         borderRadius: '5px',
                //         cursor: 'default',
                //         '&:hover': {
                //           backgroundColor: 'transparent',
                //         },
                //       }}>
                //       {renderSortIcon(column.field)}
                //     </IconButton>
                //   )}
                // </TableCell>

                <TableCell
                  key={column.field}
                  style={{
                    maxWidth: column.maxWidth || 'auto',
                    width: column.width,
                    minWidth: column.minWidth,
                    textTransform: 'uppercase',
                    padding: headerCellPadding,
                    textWrap: 'nowrap',
                    border: '1px solid #EDF2F7'
                  }}
                >
                  {column.disableTooltip ? (
                    <Box
                      component="span"
                      style={{
                        cursor: column.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      {...(column.sortable && handleSort && {
                        onClick: () => handleSort(column.field),
                      })}
                    >
                      {column.headerName}
                    </Box>
                  ) : (
                    <Tooltip title={column.tooltip || column.headerName} arrow>
                      <Box
                        component="span"
                        style={{
                          cursor: column.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                        {...(column.sortable && handleSort && {
                          onClick: () => handleSort(column.field),
                        })}
                      >
                        {column.headerName}
                      </Box>
                    </Tooltip>
                  )}
                  {column.filterable && (
                    <Tooltip title="Filter" arrow>
                      <IconButton
                        onClick={(event) => handleOpenFilterModal(event, column)}
                        style={{
                          padding: 0,
                          marginLeft: '5px',
                        }}
                      >
                        <FilterIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {column.sortable && handleSort && (
                    <Tooltip title="Sort" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort(column.field);
                        }}
                        style={{
                          padding: '0 5px',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          // '&:hover': { backgroundColor: 'transparent' },
                        }}
                      >
                        {renderSortIcon(column.field)}
                      </IconButton>
                    </Tooltip>
                  )}

                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {loading ? (
            <TableRowsLoader
              rowsNum={10}
              columnsNum={
                filteredColumns.length +
                (showCheckBoxes ?? 0) +
                (showFavouriteButtons ?? 0)
              }
            />
          ) : (
            <TableBody>
              {data?.length > 0 ? (
                data?.map((row, rowIndex) => (
                  <TableRow
                    key={row.id ?? row._id ?? `row-${rowIndex}`}
                    onClick={() => handleRowClick(row)}
                    style={{
                      cursor: handleOnClick ? 'pointer' : 'default',
                      ...(getRowStyle ? getRowStyle(row) : {}),
                    }}
                  >
                    {showCheckBoxes && (
                      <TableCell
                        // width='18px'
                        style={{
                          borderRight: '1px solid #EDF2F7',
                          width: '35px',
                          minWidth: '35px',
                          maxWidth: '35px',
                          padding: '4px',
                          textAlign: 'center'
                        }}
                        onClick={(event: React.MouseEvent) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        {/* <Box
                          style={{
                            width: '18px',
                            minWidth: '18px'
                          }}> */}
                        <Checkbox
                          checked={isSelected(selectRowData ? row : row.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() =>
                            handleSelectRow(selectRowData ? row : row.id)
                          }
                        />
                        {/* </Box> */}
                      </TableCell>
                    )}
                    {showFavouriteButtons && (
                      <TableCell
                        padding="checkbox"
                        style={{
                          padding: '0 10px',
                          borderRight: '1px solid #EDF2F7',
                          zIndex: '99999',
                        }}
                        onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          icon={<Star />}
                          checkedIcon={<FilledStart />}
                          checked={isFavourite(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleClickFavourite(row.id)}
                        />
                      </TableCell>
                    )}
                    {filteredColumns.map((column) => (
                      <TableCell
                        key={column.field}
                        style={{
                          maxWidth: column.maxWidth || 'auto',
                          width: column.width || 'auto',
                          minWidth: column.minWidth,
                          padding: bodyCellPadding,
                          fontSize: '12px',
                          border: '1px solid #EDF2F7',
                          ...(column.maxWidth
                            ? { overflow: 'hidden', verticalAlign: 'middle' }
                            : {}),
                        }}>
                        {renderCell(column, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={
                      filteredColumns.length +
                      (showCheckBoxes ?? 0) +
                      (showFavouriteButtons ?? 0)
                    }
                    style={{
                      textAlign: 'center',
                    }}>
                    {' '}
                    No data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {showPagination && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0px 24px',
          }}>
          <Typography className={classes.description} color="#7A7A9D!important">
            {`Showing ${data?.length || 0
              } items out of ${totalRecords} results found`}
          </Typography>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100, 500, 1000]} // Adjust as necessary
            component="div"
            count={totalRecords} // totalRecords should be the total number of records from the API
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeLimit}
            labelDisplayedRows={({ count }) =>
              `Page ${page + 1} of ${Math.ceil(count / limit)}`
            }
          // Either add proper conditions or comment it for now - Sheryar
          // backIconButtonProps={{
          //   disabled: loading,
          // }}
          // nextIconButtonProps={{
          //   disabled: loading,
          // }}
          />
        </Box>
      )}
      <Modal open={modalOpen} onClose={handleCloseFilterModal}>
        <Box
          sx={{
            position: 'absolute',
            top: filterAnchorEl ? filterAnchorEl.top : '50%',
            left: filterAnchorEl ? filterAnchorEl.left : '50%',
            transform: 'translateY(0)',
            bgcolor: '#FFFFFF',
            boxShadow: 24,
            borderRadius: '8px',
            minWidth: '214px',
            maxHeight: '50vh',
            overflow: 'auto',
          }}>
          <Box
            className={classes.smallHeading}
            style={{
              color: '#7A7A9D',
              position: 'sticky',
              top: 0,
              padding: '14px 12px',
              backgroundColor: '#FFFFFF',
              zIndex: 400,
              borderBottom: '1px solid lightgray',
            }}>
            Filter by {currentFilterColumn}
          </Box>
          <Box
            style={{
              padding: '10px',
              color: '#425466',
            }}>
            {filterOptions.map((option) => (
              <div
                key={option.value}
                className={classes.inputLabel}
                style={{
                  marginBottom: '5px',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                <Checkbox
                  checked={filters[currentFilterColumn]?.find(
                    (filterObj) => filterObj.value === option.value
                  )}
                  onChange={(e) =>
                    handleApplyFilter(
                      currentFilterColumn,
                      option.value,
                      option.label,
                      e.target.checked
                    )
                  }
                  style={{
                    marginRight: '10px',
                  }}
                />
                {option.label}
              </div>
            ))}
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default CustomizedTable;
